import React, { useEffect, useMemo, useState } from 'react';
import { Content } from '@backstage/core-components';
import {
  Box,
  Chip,
  Grid,
  Typography,
  CircularProgress,
} from '@material-ui/core';
import { useParams, useNavigate } from 'react-router-dom';
import SupportRequestHeader from '../../SupportRequestHeader';
import ConfirmationNumberIcon from '@material-ui/icons/ConfirmationNumber';
import ReceiptIcon from '@material-ui/icons/Receipt';
import TimerIcon from '@material-ui/icons/Timer';
import PersonIcon from '@material-ui/icons/Person';
import AssignAdminModal from './AssignAdminModal';
import EditStatusModal from '../../EditStatusModal';
import UpdatedDateIcon from '../../../icons/UpdatedDateIcon';
import CreateDateIcon from '../../../icons/CreateDateIcon';
import { RequestStatus, SupportRequest, SummaryItemConfig } from '../../types';
import { useUserDateFormatter } from '../../hooks';
import AttachmentsList from '../../AttachmentsList';
import SummaryRequestView from '../../SummaryRequestView/SummaryRequestView';
import StatusRequest from '../../StatusRequest/StatusRequest';
import DcimInfo from './DcimInfo';
import { useUserRequestsPermissions } from '../../hooks';
import {
  useSendSupportMessage,
  useSupportRequest,
  useSupportRequestAttachments,
  useSupportRequestCategories,
  useUpdateSupportRequestCategory,
  useUpdateSupportRequestStatus,
} from '../../hooks/useSupportRequests';
import {
  SectionDivider,
  FieldLabelText,
  SectionTitle,
  ContentWrapper,
} from '../../UserRequestsStyles';
import { DetailsField } from '../../DetailsField';
import ActivityPanel from '../../ActivityPanel/ActivityPanel';
import { useSupportRequestLogs } from '../../hooks/useSupportRequests';
import { FlexBox } from '../../../global/FlexBox';
import { useSnackbar } from 'notistack';
import { getApiErrorMessage } from '../../../../shared/api/getApiErrorMessage';
import { DEFAULT_CATEGORY, DEFAULT_CATEGORY_LABEL } from '../constants';
import { REQUEST_TYPE } from '../../constants';

const CATEGORY_LABELS: Record<string, string> = {
  [DEFAULT_CATEGORY]: DEFAULT_CATEGORY_LABEL,
};

export function getCategoryLabel(category?: string | null): string {
  const value = category || DEFAULT_CATEGORY;
  return CATEGORY_LABELS[value] ?? value;
}

function getInitialCategory(category?: string | null): string {
  return category || DEFAULT_CATEGORY;
}

const getSummaryItemsConfig = (
  request: SupportRequest,
  formatDate: (dateIso?: string | null) => string,
): SummaryItemConfig[] => [
  {
    icon: <ConfirmationNumberIcon />,
    label: 'Ticket Number',
    value: `${request.requestId || request.id}`,
  },
  {
    icon: <ReceiptIcon />,
    label: 'Ticket Type',
    value: request.ticketType,
  },
  {
    icon: <TimerIcon />,
    label: 'Status',
    value: <StatusRequest status={request.status} />,
  },
  {
    icon: <PersonIcon />,
    label: 'Assigned Admin',
    value: request.assignedAdmin ?? '-',
  },
  {
    icon: <CreateDateIcon />,
    label: 'Create Date',
    value: formatDate(request.createDate),
  },
  {
    icon: <UpdatedDateIcon />,
    label: 'Updated On',
    value: formatDate(request.updatedAt),
  },
];

const SupportRequestDetails = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();

  const { formatDateTime: formatDate, formatDate: formatDateOnly } =
    useUserDateFormatter();

  const {
    data: request,
    isLoading,
    isFetching,
    isError,
    error,
  } = useSupportRequest(id);
  const { mutate: sendSupportMessage, isPending: isSendingMessage } =
    useSendSupportMessage();
  const {
    data: activityLogs,
    isLoading: isLogsLoading,
    isFetching: isLogsFetching,
    refetch: refetchActivityLogs,
  } = useSupportRequestLogs({
    supportRequestId: id,
  });
  const { data: attachments } = useSupportRequestAttachments({
    supportRequestId: id,
  });
  const {
    mutateAsync: updateSupportRequestStatus,
    isPending: isUpdatingStatus,
  } = useUpdateSupportRequestStatus();
  const {
    mutateAsync: updateSupportRequestCategory,
    isPending: isUpdatingCategory,
  } = useUpdateSupportRequestCategory();
  const { data: supportCategories = [], isLoading: isCategoriesLoading } =
    useSupportRequestCategories();

  const [isReassignOpen, setIsReassignOpen] = useState(false);
  const [isEditOpen, setIsEditOpen] = useState(false);

  const { permissions } = useUserRequestsPermissions();
  const canWrite = !!permissions.support.write;

  const openReassign = () => setIsReassignOpen(true);
  const closeReassign = () => setIsReassignOpen(false);
  const openEdit = () => setIsEditOpen(true);
  const closeEdit = () => setIsEditOpen(false);

  const handleAssign = async (category: string) => {
    if (!id) return;
    if (isUpdatingCategory) return;
    try {
      await updateSupportRequestCategory({ id, category });
      closeReassign();
    } catch (assignError) {
      const message = assignError instanceof Error ? assignError.message : '';
      if (message.toLowerCase().includes('already set to this value')) {
        closeReassign();
        return;
      }

      enqueueSnackbar(
        getApiErrorMessage(assignError, 'Failed to update support category'),
        {
          variant: 'error',
        },
      );
    }
  };

  const handleSendMessage = async (data: {
    email: string;
    message: string;
  }) => {
    if (!id) return;
    try {
      await sendSupportMessage({
        id,
        email: data.email,
        message: data.message,
      });
    } catch (messageError) {
      enqueueSnackbar(
        getApiErrorMessage(messageError, 'Failed to send message'),
        {
          variant: 'error',
        },
      );
    }
  };

  const handleSaveStatus = async (next: RequestStatus, reason?: string) => {
    if (!id) return;
    if (isUpdatingStatus) return;
    try {
      await updateSupportRequestStatus({ id, status: next, reason });
      await refetchActivityLogs();
      closeEdit();
    } catch (statusError) {
      enqueueSnackbar(
        getApiErrorMessage(statusError, 'Failed to update request status'),
        {
          variant: 'error',
        },
      );
    }
  };

  const displayRequest: SupportRequest | undefined = request;
  const initialModalCategory = getInitialCategory(displayRequest?.category);

  const isPageLoading = isLoading || isFetching;

  const activities = useMemo(
    () => activityLogs?.elements || [],
    [activityLogs],
  );
  const requestAttachments = useMemo(() => attachments || [], [attachments]);

  const isActivityLoading = isLogsLoading || isLogsFetching || isSendingMessage;

  useEffect(() => {
    if (!isError) return;

    enqueueSnackbar(
      getApiErrorMessage(error, 'Failed to load support request'),
      {
        variant: 'error',
      },
    );
  }, [enqueueSnackbar, error, isError]);

  if (isPageLoading) {
    return (
      <Content>
        <FlexBox
          sx={{
            height: '100vh',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CircularProgress />
        </FlexBox>
      </Content>
    );
  }

  if (!displayRequest) {
    return (
      <Content>
        <Typography variant="h6">Support Request not found</Typography>
      </Content>
    );
  }

  return (
    <ContentWrapper>
      <Content>
        <SupportRequestHeader
          onBack={() => navigate('/user-requests/support')}
          onReassign={openReassign}
          onEditStatus={openEdit}
          canWrite={canWrite}
        />
        <Grid container spacing={3}>
          <Grid item xs={12} md={8}>
            <SectionTitle variant="h2">Support Ticket Information</SectionTitle>
            <SummaryRequestView
              items={getSummaryItemsConfig(displayRequest, formatDate)}
            />

            <SectionDivider />

            <SectionTitle variant="h4">Ticket Details</SectionTitle>
            <Box style={{ marginTop: 16 }} />
            <Grid container>
              <Grid item xs={12} md={6}>
                <DetailsField
                  label="First Name"
                  value={displayRequest.firstName}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailsField
                  label="Last Name"
                  value={displayRequest.lastName}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailsField
                  label="Email Address"
                  value={
                    <Chip color="primary" label={displayRequest.email ?? '-'} />
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailsField
                  label="Phone Number"
                  value={displayRequest.phone}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailsField
                  label="Primary Contact Email Address"
                  value={
                    <Chip
                      color="primary"
                      label={displayRequest.primaryEmail ?? '-'}
                    />
                  }
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DetailsField
                  label="Primary Contact Phone Number"
                  value={displayRequest.primaryPhone}
                />
              </Grid>
              <Grid item xs={12}>
                <DetailsField
                  label="Brief Description"
                  value={displayRequest.description}
                />
              </Grid>
              <Grid item xs={12}>
                <DetailsField label="Message" value={displayRequest.message} />
              </Grid>
            </Grid>

            <SectionDivider />

            <Grid container>
              <Grid item xs={12} md={4}>
                <DetailsField
                  label="Support Category"
                  value={getCategoryLabel(displayRequest.category)}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DetailsField
                  label="Issue Start Time"
                  value={
                    displayRequest.issueNotTimeDependent
                      ? 'Issue is not time-dependent'
                      : formatDate(displayRequest.startUtc)
                  }
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <DetailsField
                  label="Issue End Time"
                  value={
                    displayRequest.issueNotTimeDependent
                      ? 'Issue is not time-dependent'
                      : formatDate(displayRequest.endUtc)
                  }
                />
              </Grid>
            </Grid>
            <DcimInfo request={displayRequest} />

            <SectionDivider />

            <FieldLabelText>Attachments</FieldLabelText>
            <AttachmentsList
              items={requestAttachments.map(att => ({
                ...att,
                uploadedAt: formatDateOnly(att.uploadedAt),
              }))}
            />
          </Grid>

          <Grid item xs={12} md={4} style={{ paddingBottom: 0 }}>
            <ActivityPanel
              defaultEmail={displayRequest.primaryEmail || displayRequest.email}
              activities={activities}
              onSendMessage={handleSendMessage}
              isLoading={isActivityLoading}
              readOnly={!canWrite}
              requestType={REQUEST_TYPE.SUPPORT}
            />
          </Grid>
        </Grid>
      </Content>

      <AssignAdminModal
        open={isReassignOpen}
        onClose={closeReassign}
        onAssign={handleAssign}
        categories={supportCategories}
        currentCategory={initialModalCategory}
        isCategoriesLoading={isCategoriesLoading}
        isAssigning={isUpdatingCategory}
      />
      <EditStatusModal
        open={isEditOpen}
        onClose={closeEdit}
        currentStatus={displayRequest.status}
        isLoading={isUpdatingStatus}
        onSave={handleSaveStatus}
      />
    </ContentWrapper>
  );
};

export default SupportRequestDetails;
